"use client";

import { Content } from "@/services/content/types";
import { PropsWithChildren, createContext, useContext } from "react";

export const asMenu = (entry: unknown): Content.Menu | undefined => {
  if (!entry || typeof entry !== "object") return undefined;
  const e = entry as { sys?: { contentType?: { sys?: { id?: string } } } };
  return e.sys?.contentType?.sys?.id === "menu"
    ? (entry as Content.Menu)
    : undefined;
};

export const asMenuItem = (entry: unknown): Content.MenuItem | undefined => {
  if (!entry || typeof entry !== "object") return undefined;
  const e = entry as { sys?: { contentType?: { sys?: { id?: string } } } };
  return e.sys?.contentType?.sys?.id === "menuItem"
    ? (entry as Content.MenuItem)
    : undefined;
};

const pathMatchesItem = (mi: Content.MenuItem, pathname: string): boolean => {
  const slug = mi.fields.item?.fields?.slug;
  const url = mi.fields.url;
  if (slug === "index" && pathname === "/") return true;
  if (slug && (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)))
    return true;
  if (url && (pathname === url || pathname.startsWith(`${url}/`))) return true;
  return false;
};

export const findOwnerMenuItem = (
  headerMenu: Content.Menu | undefined,
  pathname: string,
): Content.MenuItem | undefined => {
  const top = headerMenu?.fields?.menuItems ?? [];
  for (const candidate of top) {
    const mi = asMenuItem(candidate);
    if (!mi) continue;
    if (pathMatchesItem(mi, pathname)) return mi;

    const nested = mi.fields.menuItems ?? [];
    for (const sub of nested) {
      const subMenu = asMenu(sub);
      if (!subMenu) continue;
      const subItems = subMenu.fields.menuItems ?? [];
      for (const si of subItems) {
        const smi = asMenuItem(si);
        if (smi && pathMatchesItem(smi, pathname)) return mi;
      }
    }
  }
  return undefined;
};

export const getNestedMenu = (
  item: Content.MenuItem | undefined,
): Content.Menu | undefined => {
  const nested = item?.fields?.menuItems ?? [];
  for (const entry of nested) {
    const m = asMenu(entry);
    if (m) return m;
  }
  return undefined;
};

type MenuContextType = {
  menus: Content.Menu[];
};

export const MenuContext = createContext<MenuContextType>({
  menus: [],
});

type MenuContextProps = {
  menus: Content.Menu[];
};

export const MenuContextProvider = ({
  children,
  menus,
}: PropsWithChildren<MenuContextProps>) => {
  return (
    <MenuContext.Provider
      value={{
        menus,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenus = () => {
  const { menus } = useContext(MenuContext);

  return { menus };
};

export const useMenu = (id: string) => {
  const { menus } = useContext(MenuContext);

  const menu = menus.find((menu) => menu.fields.id === id);

  return menu;
};
